import { useState } from 'react'
import { router } from '@inertiajs/react'
import Layout from '../Layout'

// ---- Step labels ----
const STEP_SELECT    = 'select'
const STEP_RESOLVE   = 'resolve'
const STEP_CONFIRM   = 'confirm'

// ---- Helpers ----
function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.getAttribute('content') : ''
}

async function postJson(url, body) {
  const res = await fetch(url, {
    method:  'POST',
    headers: {
      'Content-Type':  'application/json',
      'Accept':        'application/json',
      'X-CSRF-Token':  csrfToken(),
    },
    body: JSON.stringify(body),
  })
  const json = await res.json()
  return { ok: res.ok, status: res.status, data: json }
}

// ---- Sub-components ----

function FlashAlert({ message }) {
  if (!message) return null
  return (
    <div className="px-4 py-2 bg-red-950 border border-red-800 rounded-lg text-sm text-red-400 font-mono">
      {message}
    </div>
  )
}

function SectionTitle({ children }) {
  return (
    <h3 className="text-xs font-mono font-semibold text-zinc-500 uppercase tracking-wider mb-2">
      {children}
    </h3>
  )
}

function CollisionTable({ title, rows, decisionKey, decisions, onChange, strategies }) {
  if (!rows || rows.length === 0) return null
  return (
    <div className="space-y-2">
      <SectionTitle>{title}</SectionTitle>
      <div className="border border-zinc-700 rounded-lg overflow-hidden">
        <table className="w-full text-sm font-mono">
          <thead>
            <tr className="border-b border-zinc-700 bg-zinc-800/40">
              <th className="text-left px-4 py-2 text-xs text-zinc-500 font-semibold uppercase tracking-wider">
                Origin
              </th>
              <th className="text-left px-4 py-2 text-xs text-zinc-500 font-semibold uppercase tracking-wider">
                Destination
              </th>
              <th className="px-4 py-2 text-xs text-zinc-500 font-semibold uppercase tracking-wider text-right">
                Resolution
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-700">
            {rows.map((row, idx) => {
              const key = row[decisionKey] || idx
              return (
                <tr key={key} className="hover:bg-zinc-800/20">
                  <td className="px-4 py-3 text-zinc-300 break-all max-w-xs">
                    {row.src_title || row.src_slug || row.src_id}
                    {row.topic_key && (
                      <div className="text-xs text-zinc-500 mt-0.5">{row.topic_key}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-400 break-all max-w-xs">
                    {row.dst_title || row.dst_slug || row.dst_id}
                    {row.topic_key && (
                      <div className="text-xs text-zinc-600 mt-0.5">exists in destination</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <select
                      value={decisions[key] || strategies[0]}
                      onChange={e => onChange(key, e.target.value)}
                      className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs font-mono text-zinc-200 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    >
                      {strategies.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function PersonalBlockersTable({ rows }) {
  if (!rows || rows.length === 0) return null
  return (
    <div className="space-y-2">
      <SectionTitle>Personal-scope memories (will become invisible post-transfer)</SectionTitle>
      <div className="border border-amber-800/40 rounded-lg overflow-hidden bg-amber-950/20">
        <table className="w-full text-sm font-mono">
          <thead>
            <tr className="border-b border-amber-800/40 bg-amber-900/20">
              <th className="text-left px-4 py-2 text-xs text-amber-500 font-semibold uppercase tracking-wider">
                Memory ID
              </th>
              <th className="text-left px-4 py-2 text-xs text-amber-500 font-semibold uppercase tracking-wider">
                Owner user ID
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amber-800/30">
            {rows.map(row => (
              <tr key={row.memory_id}>
                <td className="px-4 py-2 text-amber-300">{row.memory_id}</td>
                <td className="px-4 py-2 text-amber-400">{row.creator_user_id}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-amber-500 font-mono">
        These memories will remain in the destination but will only be visible to their original creator
        if that user is a member of the destination workspace.
      </p>
    </div>
  )
}

// ---- Main component ----

export default function Transfer({ source, destinations = [] }) {
  const [step,           setStep]           = useState(STEP_SELECT)
  const [destinationId,  setDestinationId]  = useState(destinations[0]?.id || '')
  const [preflightData,  setPreflightData]  = useState(null)
  const [memDecisions,   setMemDecisions]   = useState({})
  const [projDecisions,  setProjDecisions]  = useState({})
  const [confirmName,    setConfirmName]    = useState('')
  const [submitting,     setSubmitting]     = useState(false)
  const [error,          setError]          = useState(null)

  // ---- Step 1: Run preflight ----
  async function handlePreflight(e) {
    e.preventDefault()
    if (!destinationId) return
    setSubmitting(true)
    setError(null)

    const { ok, data } = await postJson(
      `/settings/workspaces/${source.id}/transfer/preflight`,
      { destination_id: destinationId }
    )

    setSubmitting(false)

    if (!ok) {
      setError(data?.errors?.join(', ') || 'Pre-flight check failed.')
      return
    }

    // Seed default decisions: skip for everything
    const memDefaults = {}
    ;(data.memory_collisions || []).forEach(row => {
      memDefaults[row.src_id] = 'skip'
    })

    const projDefaults = {}
    ;(data.project_collisions || []).forEach(row => {
      projDefaults[row.src_id] = 'skip'
    })

    setPreflightData(data)
    setMemDecisions(memDefaults)
    setProjDecisions(projDefaults)
    setStep(STEP_RESOLVE)
  }

  // ---- Step 2: Proceed to confirmation ----
  function handleResolveNext(e) {
    e.preventDefault()
    setStep(STEP_CONFIRM)
  }

  // ---- Step 3: Execute transfer ----
  function handleExecute(e) {
    e.preventDefault()

    if (confirmName !== source.name) {
      setError(`Workspace name does not match. Type "${source.name}" to confirm.`)
      return
    }

    const memoryDecisions = (preflightData?.memory_collisions || []).map(row => ({
      src_memory_id: row.src_id,
      dst_memory_id: row.dst_id,
      strategy:      memDecisions[row.src_id] || 'skip',
    }))

    setSubmitting(true)
    setError(null)

    router.post(
      `/settings/workspaces/${source.id}/transfer/execute`,
      {
        destination_id: destinationId,
        decisions:      memoryDecisions,
      },
      {
        onError:  ()  => { setSubmitting(false); setError('Transfer failed. Please try again.') },
        onFinish: ()  => setSubmitting(false),
      }
    )
  }

  // ---- Step back ----
  function goBack() {
    setError(null)
    if (step === STEP_CONFIRM) setStep(STEP_RESOLVE)
    else if (step === STEP_RESOLVE) setStep(STEP_SELECT)
  }

  const hasCollisions = preflightData && (
    (preflightData.memory_collisions?.length > 0) ||
    (preflightData.project_collisions?.length > 0) ||
    (preflightData.personal_blockers?.length > 0)
  )

  return (
    <Layout active="workspaces">
      <div className="max-w-3xl space-y-6">

        {/* Header */}
        <div>
          <h2 className="text-lg font-mono font-bold text-zinc-100">
            Transfer workspace
          </h2>
          <p className="text-sm text-zinc-500 mt-1">
            Move all memories, projects and sessions from{' '}
            <span className="text-zinc-300 font-semibold">{source.name}</span>{' '}
            into another workspace you own.
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 font-mono text-xs">
          {[
            { id: STEP_SELECT,  label: '1. Destination' },
            { id: STEP_RESOLVE, label: '2. Collisions'  },
            { id: STEP_CONFIRM, label: '3. Confirm'     },
          ].map(({ id, label }, idx, arr) => (
            <div key={id} className="flex items-center gap-2">
              <span className={
                step === id
                  ? 'text-emerald-400 font-semibold'
                  : 'text-zinc-600'
              }>
                {label}
              </span>
              {idx < arr.length - 1 && (
                <span className="text-zinc-700">&#8250;</span>
              )}
            </div>
          ))}
        </div>

        <FlashAlert message={error} />

        {/* ---- STEP 1: Select destination ---- */}
        {step === STEP_SELECT && (
          <form onSubmit={handlePreflight} className="space-y-4">
            {destinations.length === 0 ? (
              <div className="px-4 py-6 border border-zinc-700 rounded-lg text-sm text-zinc-500 font-mono text-center">
                No eligible destination workspaces. You must own at least one other company workspace.
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-xs font-mono text-zinc-400 mb-1">
                    DESTINATION WORKSPACE
                  </label>
                  <select
                    value={destinationId}
                    onChange={e => setDestinationId(e.target.value)}
                    required
                    className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm font-mono text-zinc-100 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="">Select workspace...</option>
                    {destinations.map(d => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    type="submit"
                    disabled={submitting || !destinationId}
                    className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-mono text-sm font-medium py-2 px-4 rounded transition-colors"
                  >
                    {submitting ? 'Checking...' : 'Check for collisions'}
                  </button>
                  <a
                    href="/settings/workspaces"
                    className="text-xs font-mono text-zinc-500 hover:text-zinc-300 transition-colors"
                  >
                    Cancel
                  </a>
                </div>
              </>
            )}
          </form>
        )}

        {/* ---- STEP 2: Resolve collisions ---- */}
        {step === STEP_RESOLVE && preflightData && (
          <form onSubmit={handleResolveNext} className="space-y-6">

            {!hasCollisions && (
              <div className="px-4 py-3 bg-emerald-950 border border-emerald-800 rounded-lg text-sm text-emerald-400 font-mono">
                No collisions detected. The transfer can proceed.
              </div>
            )}

            <CollisionTable
              title="Memory collisions (topic_key)"
              rows={preflightData.memory_collisions}
              decisionKey="src_id"
              decisions={memDecisions}
              onChange={(key, val) => setMemDecisions(prev => ({ ...prev, [key]: val }))}
              strategies={['skip', 'overwrite', 'stage']}
            />

            <CollisionTable
              title="Project slug collisions"
              rows={preflightData.project_collisions}
              decisionKey="src_id"
              decisions={projDecisions}
              onChange={(key, val) => setProjDecisions(prev => ({ ...prev, [key]: val }))}
              strategies={['skip', 'overwrite']}
            />

            <PersonalBlockersTable rows={preflightData.personal_blockers} />

            <div className="flex items-center gap-3">
              <button
                type="submit"
                className="bg-emerald-600 hover:bg-emerald-500 text-white font-mono text-sm font-medium py-2 px-4 rounded transition-colors"
              >
                Continue to confirmation
              </button>
              <button
                type="button"
                onClick={goBack}
                className="text-xs font-mono text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                Back
              </button>
            </div>
          </form>
        )}

        {/* ---- STEP 3: Typed confirmation ---- */}
        {step === STEP_CONFIRM && (
          <form onSubmit={handleExecute} className="space-y-6">

            <div className="border border-amber-800/50 rounded-lg p-4 bg-amber-950/20 space-y-2">
              <p className="text-sm font-mono text-amber-300 font-semibold">
                This action is irreversible.
              </p>
              <ul className="text-xs font-mono text-amber-400 space-y-1 list-disc list-inside">
                <li>All memories, projects, and sessions in <strong>{source.name}</strong> will be moved.</li>
                <li>API keys and OAuth clients for <strong>{source.name}</strong> will be revoked.</li>
                <li><strong>{source.name}</strong> will be archived and no longer active.</li>
              </ul>
            </div>

            <div>
              <label className="block text-xs font-mono text-zinc-400 mb-1">
                TYPE THE WORKSPACE NAME TO CONFIRM
              </label>
              <p className="text-xs font-mono text-zinc-600 mb-2">
                Type <span className="text-zinc-300">{source.name}</span> to enable the transfer button.
              </p>
              <input
                type="text"
                value={confirmName}
                onChange={e => setConfirmName(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm font-mono text-zinc-100 placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder={source.name}
                autoFocus
              />
            </div>

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={submitting || confirmName !== source.name}
                className="bg-red-700 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-mono text-sm font-medium py-2 px-4 rounded transition-colors"
              >
                {submitting ? 'Transferring...' : 'Transfer workspace'}
              </button>
              <button
                type="button"
                onClick={goBack}
                className="text-xs font-mono text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                Back
              </button>
            </div>
          </form>
        )}

      </div>
    </Layout>
  )
}
